import * as React from "react";
import Icon from "../Icon/Icon";
import { SGBSButtonType, SGBSColor, SGBSSize } from "../../common/common.typings";
import { classNames } from "../../common/stringUtils";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The button text to display will be overridden if children is defined
   */
  text?: string;
  /**
   * any color defined in SGBSColors
   * will be translated to btn-primary, btn-royal-blue
   * default value is "primary"
   */
  color?: SGBSColor;

  /**
   * Type of the button can be one of "default" | "outline" | "discreet" | "flat" | "toggle" | "link"
   * will be translated to  btn-default, btn-outline, btn-discreet,...
   * default value is null and will not not append
   */
  btnType?: SGBSButtonType;

  /**
   * size of the button by default "md"
   * will be translated to btn-md, btn-lg,...
   */
  size?: SGBSSize;

  /**
   * css class will be appended to any existing class
   */
  className?: string;

  /**
   * icon type by default disable
   * example: arrow_forward, arrow_back, settings
   */
  icon?: string;

  /**
   * icon position, if this one is enable, can be one of "left" | "right"
   * default position is right
   */
  iconPosition?: "left" | "right";

  /**
   * Indicates if the button should display a loading icon or not
   * use [iconPosition] prop to indicate the position of the loading icon
   */
  loading?: boolean;
}

const buildButtonClasses = (color: SGBSColor, type?: SGBSButtonType): string => {
  if (!type) {
    return `btn-${color}`;
  }

  if (type === "default") {
    return "sgbs-btn-default";
  }
  if (type === "link") {
    return `btn-link`;
  }

  return `btn-${type}-${color}`;
};

const Button: React.FC<ButtonProps> = ({
                                         btnType,
                                         size = "md",
                                         color = "primary",
                                         className = "",
                                         text = "",
                                         children = null,
                                         icon,
                                         iconPosition = "left",
                                         loading = false,
                                         ...rest
                                       }: ButtonProps) => {
  const btnClass = buildButtonClasses(color, btnType);
  const btnIconClass = iconPosition === "right" ? "btn-text-icon" : "btn-icon-text";
  const hasTextAndIcon = (text !== "" || children !== null) && (!!icon || loading);
  const loadingClass = loading ? "loading" : "";

  const loadingSpan = (
    <>
      {iconPosition === "right" && <ButtonText text={text}>{children}</ButtonText>}
      <span className="loader" />
      <i className="icon">info_outline</i>
      {iconPosition === "left" && <ButtonText text={text}>{children}</ButtonText>}
    </>
  );

  return (
    <button
      type={rest?.type ?? "button"}
      className={classNames("btn", btnClass, ` btn-${size}`, hasTextAndIcon ? btnIconClass : undefined, loadingClass, className)}
      {...rest}
    >
      {!loading && (
        <>
          {icon && iconPosition === "left" && <Icon iconName={icon} size={size} />}
          {<ButtonText text={text}>{children}</ButtonText>}
          {icon && iconPosition === "right" && <Icon iconName={icon} size={size} />}
        </>
      )}

      {loading && loadingSpan}
    </button>
  );
};

const ButtonText: React.FC<{ text?: string }> = ({ text, children }) => {
  if (children) {
    return <>{children}</>;
  }
  return text ? <>{text}</> : null;
};

export default Button;
